import { useEffect, useState } from 'react';
import { resolveLocationImage, ResolvedLocationImage } from '../services/firebaseService';

/**
 * Delt hook for ALLE kartlag: henter et stedsnøyaktig bilde (Mapillary / Street
 * View) for et koordinat-punkt når `enabled` er sant. Kalles kun ved detalj-
 * åpning (aldri for markører) — det er der kostnaden er forsvarlig. Backend
 * cacher avgjørelsen (geo-nøkkel, 180 d) så det eksterne oppslaget skjer én gang
 * per lokasjon. Returnerer null til et bilde er løst opp (eller hvis ingen finnes).
 *
 * Brukseksempel i et hvilket som helst lag-detaljkort:
 *   const locImg = useLocationImage(p.lat, p.lng, !hasOwnPhoto);
 *   if (locImg) <img src={locImg.url} /> + kreditering (locImg.credit)
 */
/**
 * Utvidet variant som også eksponerer `loading` — sant mens det eksterne
 * oppslaget pågår. Nyttig for detaljkort som vil vise en lasteanimasjon i
 * bilde-sporet i stedet for stille å vente. `useLocationImage` er en tynn
 * wrapper over denne og beholder den gamle retur-typen (bakoverkompatibel).
 */
export function useLocationImageState(
  lat?: number,
  lng?: number,
  enabled = true,
): { image: ResolvedLocationImage | null; loading: boolean } {
  const [img, setImg] = useState<ResolvedLocationImage | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setImg(null);
    if (!enabled || typeof lat !== 'number' || typeof lng !== 'number') {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    resolveLocationImage(lat, lng)
      .then((r) => {
        if (cancelled) return;
        if (r && r.source !== 'none' && r.url) setImg(r);
      })
      .catch(() => { /* stille — bilde er valgfritt */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lat, lng, enabled]);

  return { image: img, loading };
}

export function useLocationImage(
  lat?: number,
  lng?: number,
  enabled = true,
): ResolvedLocationImage | null {
  return useLocationImageState(lat, lng, enabled).image;
}
